import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { getSubDomainById } from '../../redux/subDomains/subDomainActions'
import { useAppDispatch } from '../../redux/store'
import { selectSubDomain } from '../../redux/subDomains/subDomainSelectors'
import { useTranslation } from 'react-i18next'
import { formatTitle } from '../../utils/formatTitle'
import { formatStructuredDescription } from '../../utils/formatStructuredDescription'
import {
  DetailButton,
  DetailDashGrid,
  DetailInfoGrid,
  DetailInfoItem,
  DetailKpiCard,
  DetailKpiGrid,
  DetailPageHead,
  DetailPanel,
  DetailProse,
} from '../../components/common/detail'
import '../../components/common/ListPageTable.css'

function SubDomainDetails() {
  const { id } = useParams<{ id: string }>()
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const subDomain = useSelector(selectSubDomain)
  const formattedDescription = formatStructuredDescription(
    subDomain?.Description
  )

  const from = (location.state as any)?.from as
    | {
        tab: number
        panel: string
        controlLabel: string
        familyLabel: string
        rowLabel: string
      }
    | undefined

  const maturiteBackPath = from
    ? `/maturite?tab=${from.tab}&panel=${encodeURIComponent(from.panel)}${
        from.controlLabel
          ? `&controlLabel=${encodeURIComponent(from.controlLabel)}`
          : ''
      }${
        from.familyLabel
          ? `&familyLabel=${encodeURIComponent(from.familyLabel)}`
          : ''
      }`
    : '/maturite'

  useEffect(() => {
    if (id) dispatch(getSubDomainById(id))
  }, [dispatch, id])

  const title = formatTitle(
    subDomain?.LabelSD || from?.rowLabel || t('Subdomain')
  )
  const weight = Number(subDomain?.Weight)
  const target = Number(subDomain?.TargetScore)
  const needsSetup = weight < 0 || target < 0
  const controlLabel = subDomain?.Label
    ? formatTitle(subDomain.Label)
    : from?.controlLabel
      ? formatTitle(from.controlLabel)
      : '—'
  const source = subDomain?.Source || '—'

  return (
    <div className="detail-page">
      <DetailPageHead
        eyebrow={
          from?.familyLabel
            ? formatTitle(from.familyLabel)
            : t('maturityManagementPage.title')
        }
        title={title}
        subtitle={t('subDomainDetails.subtitle')}
        actions={
          <>
            <DetailButton
              variant="ghost"
              onClick={() => navigate(maturiteBackPath)}
            >
              {t('subDomainDetails.back')}
            </DetailButton>
            {subDomain?.hasMaturityScoring ? (
              <DetailButton
                onClick={() =>
                  navigate(`/subDomain-Maturityscoring/${id}`, {
                    state: { targetScore: subDomain?.TargetScore },
                  })
                }
              >
                {t('maturityScoringPage.title')}
              </DetailButton>
            ) : null}
          </>
        }
      />

      <DetailKpiGrid columns={3}>
        <DetailKpiCard
          label={t('maturityManagementPage.tableMaturity.Weight')}
          value={needsSetup || Number.isNaN(weight) ? '—' : weight}
          description={t('subDomainDetails.weightDesc')}
        />
        <DetailKpiCard
          label={t('maturityManagementPage.tableMaturity.TargetScore')}
          value={needsSetup || Number.isNaN(target) ? '—' : target}
          description={t('subDomainDetails.targetDesc')}
        />
        <DetailKpiCard
          label={t('Source')}
          value={source}
          valueSize="sm"
          description={t('subDomainDetails.sourceDesc')}
        />
      </DetailKpiGrid>

      <DetailDashGrid>
        <DetailPanel
          className="detail-panel--sticky"
          title={t('subDomainDetails.overviewTitle')}
          subtitle={t('subDomainDetails.overviewSubtitle')}
        >
          <DetailInfoGrid variant="stack">
            <DetailInfoItem
              label={t('subDomainDetails.setupStatus')}
              value={
                <span
                  className={`detail-setup-pill${
                    needsSetup ? '' : ' detail-setup-pill--ok'
                  }`}
                >
                  {needsSetup
                    ? t('subDomainDetails.setupNeeded')
                    : t('subDomainDetails.setupReady')}
                </span>
              }
            />
            <DetailInfoItem label={t('label')} value={title} />
            <DetailInfoItem label={t('Control')} value={controlLabel} />
            <DetailInfoItem label={t('Source')} value={source} />
            <DetailInfoItem
              label={t('maturityManagementPage.tableMaturity.Weight')}
              value={
                needsSetup || Number.isNaN(weight) ? '—' : String(weight)
              }
            />
            <DetailInfoItem
              label={t('maturityManagementPage.tableMaturity.TargetScore')}
              value={
                needsSetup || Number.isNaN(target) ? '—' : String(target)
              }
            />
          </DetailInfoGrid>
        </DetailPanel>

        <DetailPanel
          className="detail-panel--prose"
          title={t('Description')}
          subtitle={t('subDomainDetails.descriptionSubtitle')}
        >
          <DetailProse
            text={formattedDescription}
            emptyLabel={t('subDomainDetails.noDescription')}
          />
        </DetailPanel>
      </DetailDashGrid>
    </div>
  )
}

export default SubDomainDetails
